import { inject, Injectable, Signal } from '@angular/core';
import { ExplorerFolder, FolderContext } from '@app/shared/types/models/files/explorer-folder.model';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { UploadData } from '@app/shared/types/models/files/upload-data.model';
import { Observable } from 'rxjs';
import { FolderDTO, FolderFileDTO } from '../dtos/folders/folder.dto';
import { FolderDataService, FolderLoadState } from '../folder-data.service';

/** Folder facade; reuses the existing file cache through FolderDataService. */
@Injectable({ providedIn: 'root' })
export class FolderActionsService {
  private readonly data = inject(FolderDataService);

  public getRoot(context: FolderContext): Signal<ExplorerFolder | undefined> {
    return this.data.getRoot(context);
  }

  public getById(folderId: string): Signal<ExplorerFolder | undefined> {
    return this.data.getById(folderId);
  }

  public getChildren(folderId: string): Signal<ExplorerFolder[]> {
    return this.data.getChildren(folderId);
  }

  public getFiles(folderId: string): Signal<FileInfo[] | undefined> {
    return this.data.getFiles(folderId);
  }

  public getRootState(context: FolderContext): Signal<FolderLoadState> {
    return this.data.getRootState(context);
  }

  public getStateById(folderId: string): Signal<FolderLoadState> {
    return this.data.getStateById(folderId);
  }

  public renameAttachment(folderId: string, fileId: string, name: string): Observable<FolderFileDTO> {
    return this.data.renameAttachment(folderId, fileId, name);
  }

  public moveAttachment(folderId: string, fileId: string, targetFolderId: string): Observable<FolderFileDTO> {
    return this.data.moveAttachment(folderId, fileId, targetFolderId);
  }

  public deleteAttachment(folderId: string, fileId: string, taskId?: string): Observable<void> {
    return this.data.deleteAttachment(folderId, fileId, taskId);
  }

  public upload(folderId: string, upload: UploadData, taskId?: string): string {
    return this.data.upload(folderId, upload, taskId);
  }

  public setExternalLink(folderId: string, url: string | null): Observable<FolderDTO> {
    return this.data.setExternalLink(folderId, url);
  }

  public initialize(context: FolderContext): Observable<FolderDTO> {
    return this.data.initialize(context);
  }

  public create(parentFolderId: string, name: string): Observable<FolderDTO> {
    return this.data.create(parentFolderId, name);
  }

  public rename(folderId: string, name: string): Observable<FolderDTO> {
    return this.data.rename(folderId, name);
  }

  public delete(folderId: string, parentFolderId: string): Observable<void> {
    return this.data.delete(folderId, parentFolderId);
  }

  public reloadRoot(context: FolderContext): void {
    this.data.reloadRoot(context);
  }

  public reload(folderId: string): void {
    this.data.reload(folderId);
  }
}
